import {
  CADENCE_MINUTES,
  CADENCE_PERIOD_MINUTES,
  parseAtPhase,
  runCadenceSchema,
} from "@Simplicity-Education/core";
import type { ScheduleEntry, Schedules } from "../tables/types";

/**
 * The rows the Schedules panel renders (SIM-3376). Separate from the component so
 * fast refresh keeps working — a component file may export only components.
 */

export interface ScheduleDef {
  cadence?: string;
  at?: string;
}

/** One table on one schedule, with every entry filed under that name. */
export interface ScheduleTable {
  name: string;
  /** More than one is the required-params multi-instance case (SIM-1369). */
  entries: ScheduleEntry[];
  /** What these entries carry, for a roster row — null when they carry nothing but the name. */
  summary: string | null;
}

export interface ScheduleRow {
  name: string;
  /** The period it runs on. Equals `name` unless the definition says otherwise. */
  cadence: string;
  at: string | null;
  tableCount: number;
  /** The schedule's roster, one element per distinct table name. */
  tables: ScheduleTable[];
  /** True when the name is not itself the period — worth showing, since it is unusual. */
  renamed: boolean;
  /** False when the scheduler has no period for `cadence`, so this schedule's tables never run. */
  runnable: boolean;
}

/**
 * Whether the scheduler can run a schedule on this period. Mirrors the resolver's own
 * `parseCadence` (scheduleHealth.ts): core can parse a monthly `at`, but no connector
 * schedule runs monthly (SIM-3571).
 */
export function isRunnableCadence(cadence: string): boolean {
  const parsed = runCadenceSchema.safeParse(cadence.toLowerCase());
  return parsed.success && CADENCE_MINUTES[parsed.data] !== null;
}

/** The periods a schedule can be given, in the order the panel offers them. */
export const RUNNABLE_CADENCES = runCadenceSchema.options.filter(isRunnableCadence);

/** The three every connector carries, first and in this order; anything else follows, sorted. */
const STANDARD_SCHEDULE_NAMES = ["hourly", "daily", "weekly"];

function rank(name: string): number {
  const i = STANDARD_SCHEDULE_NAMES.indexOf(name);
  return i === -1 ? STANDARD_SCHEDULE_NAMES.length : i;
}

/**
 * The connector's schedules: the union of definitions and buckets, standard names first.
 * The one answer to "which schedules exist" — the panel and the per-table membership
 * editor disagreed while each kept its own (SIM-3402).
 */
export function listScheduleNames(
  scheduleDefs: Record<string, unknown> | undefined,
  schedules: Record<string, unknown> | undefined,
): string[] {
  const names = new Set([...Object.keys(scheduleDefs ?? {}), ...Object.keys(schedules ?? {})]);
  return [...names].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

/** Params worth naming in a roster summary, in the order a reader scans for them. */
const SUMMARY_PARAM_KEYS = ["mode", "minStartIndex", "lookbackDays", "lookbackRows"];

/**
 * Override-filter keys in the order a reader scans for them: the ones that widen scope
 * before the ones that narrow it, because "unfiltered" changes what every other key means.
 */
const FILTER_KEYS = [
  "unfiltered",
  "disable",
  "scopeBy",
  "schoolids",
  "sinceDate",
  "schoolYearStart",
  "studentPoolFromReenrollments",
];

/** `["1","2"]` → `[1,2]`; everything else stringifies as itself. */
function filterValue(value: unknown): string {
  return Array.isArray(value) ? `[${value.join(",")}]` : String(value);
}

/**
 * The override filters themselves, not a count of them — a row that says "1 filter override"
 * forces the editor open to answer the only question being asked (SIM-3515). A `false` is
 * dropped: `unfiltered: false` is the default wearing a value.
 */
function describeOverrideFilters(entry: ScheduleEntry): string[] {
  const filters = (entry.overrides as { filters?: Record<string, unknown> } | undefined)?.filters;
  if (filters == null || typeof filters !== "object") return [];
  return FILTER_KEYS.filter((k) => filters[k] !== undefined && filters[k] !== false).map((k) =>
    filters[k] === true ? k : `${k}: ${filterValue(filters[k])}`,
  );
}

/**
 * What a table's entries carry, for the roster. Removing a table deletes all of it, so the
 * roster has to say what is being deleted — 385 of 772 production entries hold filter
 * overrides, which makes a blind bulk remove a real loss rather than a theoretical one.
 */
export function summarizeEntries(entries: ScheduleEntry[]): string | null {
  const parts: string[] = [];
  if (entries.length > 1) parts.push(`${entries.length} instances`);
  for (const key of SUMMARY_PARAM_KEYS) {
    const value = entries[0]?.[key];
    // Entries are untyped JSON; an object here would summarize as "[object Object]".
    if (["string", "number", "boolean"].includes(typeof value)) {
      parts.push(`${key}: ${value as string | number | boolean}`);
    }
  }
  // From the first entry, like the params above: the "N instances" prefix is what says
  // there is more than one, and spelling out every instance would not fit a row.
  if (entries[0]) parts.push(...describeOverrideFilters(entries[0]));
  return parts.length === 0 ? null : parts.join(" · ");
}

/**
 * A schedule's roster, one element per distinct table name. Entries sharing a name are the
 * multi-instance case and stay together, because they are added and removed together.
 */
export function buildScheduleTables(entries: ScheduleEntry[] | undefined): ScheduleTable[] {
  const byName = new Map<string, ScheduleEntry[]>();
  for (const entry of Array.isArray(entries) ? entries : []) {
    if (typeof entry?.name !== "string") continue;
    const existing = byName.get(entry.name);
    if (existing) existing.push(entry);
    else byName.set(entry.name, [entry]);
  }
  // Sorted, not in config order: the array is append-ordered, so a 32-table roster
  // otherwise lists tables in whatever sequence they were added (SIM-3511).
  return [...byName.entries()]
    .map(([name, group]) => ({ name, entries: group, summary: summarizeEntries(group) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * One row per schedule — a bucket with no definition still appears, being the
 * case most worth seeing rather than hiding.
 */
export function buildScheduleRows(
  scheduleDefs: Record<string, ScheduleDef>,
  schedules: Schedules,
): ScheduleRow[] {
  return listScheduleNames(scheduleDefs, schedules).map((name) => {
    const def = scheduleDefs?.[name];
    const entries = schedules?.[name];
    const cadence = def?.cadence ?? name;
    return {
      name,
      cadence,
      at: def?.at ?? null,
      tableCount: Array.isArray(entries) ? entries.length : 0,
      tables: buildScheduleTables(entries),
      renamed: cadence !== name,
      runnable: isRunnableCadence(cadence),
    };
  });
}

/** What a valid `at` looks like on this cadence — the placeholder and the error both use it. */
export function atShapeHint(cadence: string): string {
  const period = CADENCE_PERIOD_MINUTES[cadence];
  if (period === undefined) return "HH:MM";
  if (period === 60) return ":MM";
  if (period === 10080) return "Ddd HH:MM";
  return "HH:MM";
}

/**
 * Validate an `at` the way the resolver will read it, by calling the resolver's own
 * `parseAtPhase`. A shape valid for a different cadence is the failure worth catching:
 * `schedules` is a record, so `:45` filed under `daily` passes the schema and is then
 * unusable (SIM-3159). An empty string is valid and means "no time set".
 */
export function validateAt(value: string, cadence: string): string | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  if (!isRunnableCadence(cadence)) {
    return `"${cadence}" is not a period the scheduler knows, so it cannot be given a time.`;
  }
  if (parseAtPhase(trimmed, cadence) === null) {
    return `Not a valid ${cadence} time. Expected ${atShapeHint(cadence)}.`;
  }
  return null;
}
